/**
 * useAutocomplete Hook
 *
 * Manages autocomplete state and logic including:
 * - Debounced API calls
 * - Request cancellation
 * - Hesitation detection
 * - Keyboard navigation
 * - Local embeddings with localStorage caching (optional)
 *
 * IMPORTANT: Never flicker the dropdown. Keep showing results while loading.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchSuggestions } from './api';
import {
  getLocalAutocomplete,
  isLocalEmbeddingsUnavailableError,
  THRESHOLD_HIGH,
} from './localEmbeddings';

// Debug logging - only in dev mode
const debug = import.meta.env.DEV ? (...args) => console.log(...args) : () => {};

const DEBOUNCE_MS = 500;
const MIN_QUERY_LENGTH = 3;
const AUTO_HIDE_MS = 8000; // Auto-hide suggestions after 8 seconds of inactivity

/**
 * Hook for autocomplete functionality.
 *
 * @param {string} clientId - Client identifier
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Enable/disable autocomplete (default: true)
 * @param {number} options.limit - Max suggestions (default: 5)
 * @param {boolean} options.useLocalEmbeddings - Use local embeddings (default: false)
 * @returns {Object} Autocomplete state and handlers
 */
export function useAutocomplete(clientId, options = {}) {
  const { enabled = true, limit = 5, useLocalEmbeddings = false } = options;
  const [localDisabled, setLocalDisabled] = useState(false);
  const localEnabled = useLocalEmbeddings && !localDisabled;

  // State
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [timing, setTiming] = useState(null);

  // Local embeddings state
  const [localReady, setLocalReady] = useState(false);
  const [localInitializing, setLocalInitializing] = useState(false);
  const [localQueryCount, setLocalQueryCount] = useState(0);

  // Refs for debouncing and cancellation
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const autoHideTimerRef = useRef(null); // Auto-hide after inactivity
  const lastQueryRef = useRef('');
  const lastSuccessfulQueryRef = useRef(''); // Track last query that returned results
  const localAutocompleteRef = useRef(null);

  useEffect(() => {
    debug(
      `🎛️ [AUTOCOMPLETE] Mode: ${localEnabled ? 'LOCAL EMBEDDINGS' : 'SERVER-SIDE'} | enabled=${enabled} | limit=${limit} | client=${clientId}`,
    );
  }, [clientId, enabled, limit, localEnabled]);

  /**
   * Start or reset the auto-hide timer.
   * Called when suggestions are shown or when user interacts.
   */
  const resetAutoHideTimer = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
    }
    autoHideTimerRef.current = setTimeout(() => {
      debug(`⏱️ [HOOK] Auto-hiding suggestions after ${AUTO_HIDE_MS}ms inactivity`);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, AUTO_HIDE_MS);
  }, []);

  // Auto-hide timer: start when suggestions become visible
  useEffect(() => {
    if (showSuggestions && suggestions.length > 0) {
      resetAutoHideTimer();
    } else if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, [showSuggestions, suggestions.length, resetAutoHideTimer]);

  /**
   * Initialize local embeddings (lazy, on first input focus).
   */
  const initializeLocal = useCallback(async () => {
    if (!localEnabled || localReady || localInitializing) {
      return;
    }

    debug(`📚 [HOOK] Initializing local embeddings for client: ${clientId}`);
    setLocalInitializing(true);

    try {
      const localAc = getLocalAutocomplete(clientId);
      localAutocompleteRef.current = localAc;

      const result = await localAc.initialize((current, total) => {
        debug(`📚 [HOOK] Embedding progress: ${current}/${total}`);
      });

      debug(
        `📚 [HOOK] Local initialized: fromCache=${result.fromCache}, queryCount=${result.queryCount}`,
      );
      setLocalQueryCount(result.queryCount);
      setLocalReady(true);
    } catch (error) {
      setLocalDisabled(true);
      if (isLocalEmbeddingsUnavailableError(error)) {
        debug(`📚 [HOOK] Local embeddings unavailable in this environment, using server fallback`);
      } else {
        console.error(`📚 [HOOK] Local initialization failed:`, error);
      }
      // Fall back to server mode
    } finally {
      setLocalInitializing(false);
    }
  }, [clientId, localEnabled, localReady, localInitializing]);

  const syncLocal = useCallback(async () => {
    if (!localEnabled || !localReady || localInitializing || !localAutocompleteRef.current) {
      return;
    }

    debug(`📚 [HOOK] Checking local autocomplete freshness for client: ${clientId}`);
    setLocalInitializing(true);

    try {
      const result = await localAutocompleteRef.current.ensureFresh((current, total) => {
        debug(`📚 [HOOK] Refresh progress: ${current}/${total}`);
      });

      setLocalQueryCount(result.queryCount || 0);
      setLocalReady(true);

      if (result.updated) {
        debug(`📚 [HOOK] Local autocomplete refreshed, queryCount=${result.queryCount}`);
      }
    } catch (error) {
      console.error(`📚 [HOOK] Local freshness check failed:`, error);
    } finally {
      setLocalInitializing(false);
    }
  }, [clientId, localEnabled, localReady, localInitializing]);

  /**
   * Fetch suggestions using server API.
   */
  const doFetchServer = useCallback(
    async (query) => {
      debug(`🔍 [HOOK-SERVER] Fetching from server: "${query}"`);

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const result = await fetchSuggestions(
        query,
        clientId,
        limit,
        abortControllerRef.current.signal,
      );

      debug(
        `🔍 [HOOK-SERVER] Got ${result.suggestions?.length || 0} suggestions in ${result.timing_ms}ms`,
      );
      return result;
    },
    [clientId, limit],
  );

  /**
   * Fetch suggestions using local embeddings.
   */
  const doFetchLocal = useCallback(
    async (query) => {
      debug(`🔍 [HOOK-LOCAL] Searching locally: "${query}"`);

      if (!localAutocompleteRef.current) {
        debug(`🔍 [HOOK-LOCAL] Local autocomplete not initialized`);
        return null;
      }

      const result = await localAutocompleteRef.current.search(query, limit, THRESHOLD_HIGH);
      debug(
        `🔍 [HOOK-LOCAL] Got ${result.suggestions?.length || 0} suggestions in ${result.timing_ms}ms`,
      );

      return result;
    },
    [limit],
  );

  /**
   * Fetch suggestions for query (internal).
   *
   * CRITICAL: Never hide suggestions while user is typing.
   * Only clear when query is too short or user explicitly dismisses.
   */
  const doFetch = useCallback(
    async (query) => {
      debug(
        `🔍 [HOOK] doFetch: "${query}" enabled=${enabled} length=${query.length} useLocal=${localEnabled} localReady=${localReady}`,
      );

      // Only clear suggestions when query is too short
      if (!enabled || query.length < MIN_QUERY_LENGTH) {
        debug(`🔍 [HOOK] Query too short, clearing suggestions`);
        setSuggestions([]);
        setShowSuggestions(false);
        lastSuccessfulQueryRef.current = '';
        return;
      }

      // Show loading but KEEP existing suggestions visible (no flicker!)
      setIsLoading(true);

      try {
        let result;

        // Use local if available, otherwise fall back to server
        if (localEnabled && localReady) {
          try {
            result = await doFetchLocal(query);
          } catch (error) {
            console.error(`🔍 [HOOK-LOCAL] Local search failed, using server fallback:`, error);
            result = null;
          }
        }

        const localSuggestionCount = result?.suggestions?.length || 0;

        // Fall back to server if local is unavailable, fails, or returns no suggestions.
        // This keeps local embeddings best-effort instead of authoritative.
        if (!result || localSuggestionCount === 0) {
          if (localEnabled && localReady) {
            debug(`🔍 [HOOK] Local returned no suggestions, checking server for "${query}"`);
          }
          result = await doFetchServer(query);
        }

        debug(
          `🔍 [HOOK] Got ${result.suggestions?.length || 0} suggestions in ${result.timing_ms}ms`,
        );

        // Only update if we got results OR if this is a fresh query (not a refinement)
        // This prevents flickering when intermediate queries return fewer results
        if (result.suggestions?.length > 0) {
          setSuggestions(result.suggestions);
          setTiming(result.timing_ms);
          setShowSuggestions(true);
          setSelectedIndex(-1);
          lastSuccessfulQueryRef.current = query;
        } else if (suggestions.length === 0) {
          // No existing suggestions and no new ones - hide dropdown
          setShowSuggestions(false);
        }
        // If we have existing suggestions but new query returns none,
        // KEEP showing old suggestions (user is refining their query)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[HOOK] Fetch error:', error);
        } else {
          debug(`🔍 [HOOK] Request aborted (new query)`);
        }
        // On error, keep showing existing suggestions (don't flicker)
      } finally {
        setIsLoading(false);
      }
    },
    [clientId, enabled, localEnabled, localReady, doFetchLocal, doFetchServer, suggestions.length],
  );

  /**
   * Handle query change (debounced).
   */
  const handleQueryChange = useCallback(
    (query) => {
      debug(
        `🔍 [HOOK] handleQueryChange: "${query}" (length: ${query.length}, min: ${MIN_QUERY_LENGTH})`,
      );
      lastQueryRef.current = query;

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // IMMEDIATELY hide suggestions when query is too short (no debounce delay)
      // This prevents stale suggestions from appearing when input is cleared
      if (query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setShowSuggestions(false);
        lastSuccessfulQueryRef.current = '';
        return; // No need to debounce an empty/short query
      }

      // Debounce the fetch
      debounceTimerRef.current = setTimeout(() => {
        doFetch(query);
      }, DEBOUNCE_MS);
    },
    [doFetch],
  );

  /**
   * Handle input focus - trigger lazy initialization.
   */
  const handleFocus = useCallback(() => {
    debug(
      `🔍 [HOOK] handleFocus: useLocal=${localEnabled}, localReady=${localReady}, localInitializing=${localInitializing}`,
    );
    if (localEnabled && !localReady && !localInitializing) {
      initializeLocal();
      return;
    }

    if (localEnabled && localReady && !localInitializing) {
      syncLocal();
    }
  }, [localEnabled, localReady, localInitializing, initializeLocal, syncLocal]);

  /**
   * Handle keyboard navigation.
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (!showSuggestions || suggestions.length === 0) {
        return false; // Not handled
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          resetAutoHideTimer(); // User is interacting, reset timer
          setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
          return true;

        case 'ArrowUp':
          e.preventDefault();
          resetAutoHideTimer(); // User is interacting, reset timer
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          return true;

        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          setSelectedIndex(-1);
          return true;

        case 'Enter':
          if (selectedIndex >= 0) {
            e.preventDefault();
            return { selected: suggestions[selectedIndex] };
          }
          return false;

        default:
          return false;
      }
    },
    [showSuggestions, suggestions, selectedIndex, resetAutoHideTimer],
  );

  /**
   * Select a suggestion.
   */
  const selectSuggestion = useCallback((suggestion) => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
    return suggestion;
  }, []);

  /**
   * Close suggestions and cancel any pending requests.
   * CRITICAL: Must cancel debounce timer AND abort in-flight requests
   * to prevent race conditions where suggestions reappear after close.
   */
  const closeSuggestions = useCallback(() => {
    // Cancel debounce timer to prevent pending fetch from reopening
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Cancel auto-hide timer
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    // Abort any in-flight API request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setShowSuggestions(false);
    setSelectedIndex(-1);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, []);

  return {
    // State
    suggestions,
    isLoading,
    showSuggestions,
    selectedIndex,
    timing,

    // Local embeddings state
    localReady,
    localInitializing,
    localQueryCount,

    // Handlers
    handleQueryChange,
    handleKeyDown,
    handleFocus,
    selectSuggestion,
    closeSuggestions,
    resetAutoHideTimer, // Call on mouse activity to keep suggestions visible

    // Utilities
    initializeLocal,
  };
}
